import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Share2, Download, Check, Play } from 'lucide-react';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Skeleton, EmptyState } from '@techsofit/ui';
import { WrappedView } from '../components/WrappedView';
import { GlowUpStory } from '../components/GlowUpStory';
import { useAsync } from '../lib/useAsync';
import { getMemberWrapped, shareMemberWrapped } from '../lib/gymApi';
import { saveNodeAsImage, shareOrCopy } from '../lib/share';

export function WrappedPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: wrapped, loading, error } = useAsync(() => getMemberWrapped(id), [id]);
  const cardRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState<'share' | 'image' | null>(null);
  const [copied, setCopied] = useState(false);
  const [playing, setPlaying] = useState(false);

  async function onShare() {
    setBusy('share');
    try {
      const { url } = await shareMemberWrapped(id);
      const how = await shareOrCopy(url, `${wrapped?.memberName}'s progress at ${wrapped?.gymName}`);
      if (how === 'copied') {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      if (err instanceof ApiClientError) alert(err.message);
    } finally {
      setBusy(null);
    }
  }

  async function onSaveImage() {
    if (!cardRef.current) return;
    setBusy('image');
    try {
      await saveNodeAsImage(cardRef.current, `${(wrapped?.memberName ?? 'progress').replace(/\s+/g, '-').toLowerCase()}-glow-up.png`);
    } catch {
      alert('Could not generate the image. Try sharing the link instead.');
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <section className="animate-fade-up">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="mx-auto mt-4 h-[28rem] max-w-md" />
      </section>
    );
  }
  if (error || !wrapped) {
    return (
      <section className="animate-fade-up">
        <BackLink onClick={() => navigate(`/members/${id}`)} />
        <EmptyState title="No recap yet" description={error ?? 'Add a goal and a few check-ins first.'} />
      </section>
    );
  }

  return (
    <section className="animate-fade-up">
      <BackLink onClick={() => navigate(`/members/${id}`)} />

      <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
        <Button onClick={() => setPlaying(true)}>
          <Play className="size-4" aria-hidden /> Play Glow Up
        </Button>
        <Button variant="outline" onClick={onShare} loading={busy === 'share'}>
          {copied ? <Check className="size-4" aria-hidden /> : <Share2 className="size-4" aria-hidden />}
          {copied ? 'Link copied' : 'Share'}
        </Button>
        <Button variant="outline" onClick={onSaveImage} loading={busy === 'image'}>
          <Download className="size-4" aria-hidden /> Save image
        </Button>
      </div>

      <WrappedView ref={cardRef} wrapped={wrapped} />

      {playing && (
        <GlowUpStory wrapped={wrapped} onClose={() => setPlaying(false)} onShare={onShare} onSaveImage={onSaveImage} />
      )}
    </section>
  );
}

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
      <ArrowLeft className="size-4" aria-hidden /> Member
    </button>
  );
}
